import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  FEC_DEFAULT_CYCLE,
  compactMoney,
  money,
  isValidUrl,
  loadCandidatePageData,
} from "../utils/fecApi";
import NoCivixAccountModal from "../components/People/NoCivixAccountModal";
import { verifyUrl } from "../utils/issueReportApi";
import { theme } from "../theme/colors";

const SITE_URL = "https://civixthread.com";

const PARTY_CONFIG = {
  DEM: { label: "Democrat", className: "bg-blue-50 text-blue-700 border-blue-200" },
  D: { label: "Democrat", className: "bg-blue-50 text-blue-700 border-blue-200" },
  REP: { label: "Republican", className: "bg-red-50 text-red-700 border-red-200" },
  R: { label: "Republican", className: "bg-red-50 text-red-700 border-red-200" },
  IND: { label: "Independent", className: "bg-violet-50 text-violet-700 border-violet-200" },
  LIB: { label: "Libertarian", className: "bg-amber-50 text-amber-800 border-amber-200" },
  GRN: { label: "Green", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const OFFICE_CONFIG = {
  H: { label: "House of Representatives", shortLabel: "House" },
  S: { label: "U.S. Senate", shortLabel: "Senate" },
  P: { label: "President", shortLabel: "President" },
};

function StatCard({ label, value, hint }) {
  return (
    <div className="py-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
      {hint ? <div className="mt-2 text-sm text-slate-500">{hint}</div> : null}
    </div>
  );
}

function CommitteeCard({ committee }) {
  const fecUrl = committee.id
    ? `https://www.fec.gov/data/committee/${encodeURIComponent(committee.id)}/`
    : null;

  return (
    <article className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-900">
            {fecUrl ? (
              <a href={fecUrl} target="_blank" rel="noreferrer" className="underline-offset-2 hover:text-emerald-700 hover:underline">
                {committee.name}
              </a>
            ) : (
              committee.name
            )}
          </h3>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
            {committee.id ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium">
                ID: <span className="font-mono">{committee.id}</span>
              </span>
            ) : null}
            {committee.designation ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium">
                Designation: {committee.designation}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {committee.treasurer ? (
        <div className="mt-3 text-sm text-slate-500">Treasurer: {committee.treasurer}</div>
      ) : null}
    </article>
  );
}

function PACSection({ title, subtitle, items, total, tone }) {
  const toneStyles =
    tone === "pro"
      ? {
          leftBorder: "border-l-4 border-l-emerald-500 pl-4",
          text: "text-emerald-700",
          total: "bg-emerald-700",
          emptyIcon: "Supporting",
        }
      : {
          leftBorder: "border-l-4 border-l-red-500 pl-4",
          text: "text-red-700",
          total: "bg-red-600",
          emptyIcon: "Opposing",
        };

  return (
    <section className="border-b border-slate-200 pb-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-black tracking-tight text-slate-900">{title}</h2>
        <p className="text-sm text-slate-600">{subtitle}</p>
      </div>

      {items.length > 0 ? (
        <div className="mt-5 space-y-2">
          {items.slice(0, 3).map((pac, index) => (
            <div
              key={`${pac.committee_name || "pac"}-${index}`}
              className={classNames("flex items-center justify-between border-b border-slate-100 py-3", toneStyles.leftBorder)}
            >
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900">{pac.committee_name || "Unknown PAC"}</div>
              </div>
              <div className={classNames("whitespace-nowrap text-sm font-bold shrink-0", toneStyles.text)}>
                {compactMoney(pac.total_expenditure)}
              </div>
            </div>
          ))}

          <div className={classNames("mt-4 flex items-center justify-between px-4 py-3 text-white", toneStyles.total)}>
            <span className="text-sm font-semibold">{tone === "pro" ? "Total Supporting" : "Total Opposing"}</span>
            <span className="text-lg font-black">{compactMoney(total)}</span>
          </div>
        </div>
      ) : (
        <div className="mt-5 py-4 text-sm text-slate-600">
          No {toneStyles.emptyIcon.toLowerCase()} PAC spending reported.
        </div>
      )}
    </section>
  );
}

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedTotalRaised = location.state?.totalRaised ?? null;

  const [candidate, setCandidate] = useState(null);
  const [totals, setTotals] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [superPACData, setSuperPACData] = useState(null);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [checkingWebsite, setCheckingWebsite] = useState(false);
  const [websiteUnavailableOpen, setWebsiteUnavailableOpen] = useState(false);
  const [pendingWebsiteUrl, setPendingWebsiteUrl] = useState(null);

  const loadData = useCallback(async (signal) => {
    if (!candidateId) {
      setError("Candidate ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await loadCandidatePageData(candidateId, FEC_DEFAULT_CYCLE, signal);
      if (!data.candidate) {
        throw new Error("Failed to load candidate information.");
      }
      setCandidate(data.candidate);
      setTotals(data.totals);
      setContributors(data.contributors);
      setSuperPACData(data.superPACData);
      setCommittees(data.committees);
    } catch (err) {
      if (signal?.aborted) return;
      setError(err?.message || "Failed to load candidate details.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  const websiteData = useMemo(() => {
    let rawUrl = candidate?.website || null;
    if (!rawUrl) {
      const w = committees.find((c) => c.website)?.website;
      rawUrl = w || null;
    }
    if (!rawUrl) return { url: null, isValid: false, hasValue: false };
    const valid = isValidUrl(rawUrl);
    return { url: valid ? rawUrl : null, isValid: valid, hasValue: true, rawValue: rawUrl };
  }, [candidate?.website, committees]);

  const website = websiteData.url;

  const openCampaignSite = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shouldOpenCampaignSiteDirectly = (result) => {
    if (result.safe_to_open || result.ok) return true;
    // Backend or link-check API unreachable — don't block the user.
    if (result.reason === "network_error") return true;
    // HEAD not supported; normal browser navigation often still works.
    if (result.status_code === 405 || result.status_code === 501) return true;
    return false;
  };

  const handleViewCampaignSite = async () => {
    if (!website) return;
    let url = website;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }

    setCheckingWebsite(true);
    try {
      // Server-side check — browser HEAD fails on most external sites (CORS).
      const result = await verifyUrl(url);

      if (shouldOpenCampaignSiteDirectly(result)) {
        openCampaignSite(url);
        return;
      }

      setPendingWebsiteUrl(url);
      setWebsiteUnavailableOpen(true);
    } catch {
      openCampaignSite(url);
    } finally {
      setCheckingWebsite(false);
    }
  };

  const handleCivixProfile = () => {
    if (candidate?.hasCivixThreadAccount && candidate?.civixThreadUserId) {
      navigate(`/profile/${candidate.civixThreadUserId}`);
    } else {
      setShowAccountModal(true);
    }
  };

  const partyConfig = PARTY_CONFIG[candidate?.party] || PARTY_CONFIG.IND;

  const majorContributors = useMemo(
    () =>
      contributors
        .filter((item) => Number(item.total_contribution || 0) >= 50000)
        .slice(0, 3),
    [contributors]
  );

  const proPACs = Array.isArray(superPACData?.pro) ? superPACData.pro : [];
  const antiPACs = Array.isArray(superPACData?.anti) ? superPACData.anti : [];
  const totalProSpending = Number(superPACData?.total_pro || 0);
  const totalAntiSpending = Number(superPACData?.total_anti || 0);
  const spentPercentage =
    totals?.receipts > 0 ? Math.min(100, Math.round((totals.disbursements / totals.receipts) * 100)) : 0;
  const otherFundingPercentage = Math.max(
    0,
    Math.round((100 - Number(totals?.individual_percentage || 0) - Number(totals?.pac_percentage || 0)) * 10) / 10
  );
  const fecCandidateUrl = `https://www.fec.gov/data/candidate/${encodeURIComponent(candidateId || "")}/`;
  const canonicalUrl = `${SITE_URL}/candidates/${encodeURIComponent(candidateId || "")}`;
  const displayTotalRaised = totals?.receipts ?? passedTotalRaised;
  const pageTitle = candidate
    ? `${candidate.name} — ${candidate.districtLabel || candidate.state} ${candidate.officeLabel} Candidate | CivixThread`
    : "Federal Candidate | CivixThread";
  const metaDescription = candidate
    ? `${candidate.name} (${PARTY_CONFIG[candidate.party]?.label || candidate.party}) — ${FEC_DEFAULT_CYCLE} cycle fundraising: ${compactMoney(displayTotalRaised)} raised. FEC-sourced campaign finance data on CivixThread.`
    : "Federal candidate campaign finance data from the FEC on CivixThread.";

  if (loading && !candidate) {
    return (
      <main className="flex min-h-[50vh] flex-col items-center justify-center bg-slate-50 px-6 text-slate-900">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <CircularProgress size={40} sx={{ color: theme.primary }} />
        <Typography sx={{ mt: 2, color: "#0f172a", fontWeight: 600 }}>Loading candidate funding…</Typography>
        <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 14 }}>Fetching FEC data</Typography>
      </main>
    );
  }

  if (error && !candidate) {
    return (
      <main className="flex min-h-[50vh] flex-col items-center justify-center bg-slate-50 px-6 text-slate-900">
        <Helmet>
          <title>Candidate not found | CivixThread</title>
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: 18, mb: 1 }}>Failed to load candidate</Typography>
        <Typography sx={{ color: "#64748b", textAlign: "center", mb: 3 }}>{error}</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => loadData()}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "12px",
            background: theme.gradient,
            "&:hover": { opacity: 0.9, background: theme.gradient },
          }}
        >
          Try Again
        </Button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <section className="py-10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="border-b border-slate-200 pb-8">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-700">
              {FEC_DEFAULT_CYCLE} election cycle
            </p>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <h1 className="break-words text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                  {candidate?.name || candidateId}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {candidate?.party ? (
                    <span className={classNames("rounded-full border px-3 py-1 text-xs font-semibold", partyConfig.className)}>
                      {partyConfig.label}
                    </span>
                  ) : null}
                  {candidate?.officeCode ? (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {OFFICE_CONFIG[candidate.officeCode]?.label || candidate.officeLabel}
                    </span>
                  ) : null}
                  {candidate?.districtLabel ? (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {candidate.districtLabel}
                    </span>
                  ) : null}
                  {candidate?.incumbent ? (
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Incumbent
                    </span>
                  ) : null}
                  {candidate?.openSeat ? (
                    <span className="rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-semibold text-fuchsia-700">
                      Open Seat
                    </span>
                  ) : null}
                </div>

                {candidate?.status ? (
                  <div className="mt-3 text-sm text-slate-500">FEC status: {candidate.status}</div>
                ) : null}
              </div>

              <div className="flex flex-col gap-3">
                {website ? (
                  <button
                    type="button"
                    onClick={handleViewCampaignSite}
                    disabled={checkingWebsite}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-70"
                  >
                    {checkingWebsite ? "Checking…" : "Visit Campaign Website"}
                    {!checkingWebsite ? (
                      <FaExternalLinkAlt className="h-3 w-3" aria-hidden="true" />
                    ) : null}
                  </button>
                ) : websiteData.hasValue ? (
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900">
                    Invalid Address
                  </p>
                ) : (
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Cannot determine candidate site from the FEC
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleCivixProfile}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-white px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-50"
                >
                  <AccountCircleIcon sx={{ fontSize: 18 }} />
                  CivixThread Profile
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 border-b border-slate-200 pb-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg text-emerald-700">$</span>
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">Total Raised</span>
              </div>
              <div className="text-2xl font-black text-emerald-700">{compactMoney(displayTotalRaised)}</div>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Money collected from individual donors, PACs, and party committees for this campaign.
            </p>
          </div>

          {totals && totals.receipts > 0 && (totals.individual_percentage > 0 || totals.pac_percentage > 0) ? (
            <section className="mt-8 border-b border-slate-200 pb-8">
              <h2 className="text-xl font-black tracking-tight text-slate-900">Funding Sources</h2>
              <p className="mt-1 text-sm text-slate-600">Percentage of contributions from individuals, PACs, and other sources.</p>

              <div className="mt-5 h-4 overflow-hidden rounded-full bg-slate-100">
                {totals.individual_percentage > 0 ? (
                  <div className="h-full bg-emerald-700" style={{ width: `${totals.individual_percentage}%`, float: "left" }} />
                ) : null}
                {totals.pac_percentage > 0 ? (
                  <div className="h-full bg-amber-500" style={{ width: `${totals.pac_percentage}%`, float: "left" }} />
                ) : null}
                {otherFundingPercentage > 0 ? (
                  <div className="h-full bg-slate-400" style={{ width: `${otherFundingPercentage}%`, float: "left" }} />
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-700" />
                  Individual: {totals.individual_percentage}%
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-800">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                  PAC: {totals.pac_percentage}%
                </span>
                {otherFundingPercentage > 0 ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                    Other: {otherFundingPercentage}%
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}

          <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Total Raised" value={money(totals?.receipts)} />
            <StatCard label="Total Spent" value={money(totals?.disbursements)} />
            <StatCard label="Cash on Hand" value={money(totals?.cash_on_hand_end_period)} />
            <StatCard label="Debt Owed" value={money(totals?.debts_owed_by_committee)} hint={totals?.cycle ? `Cycle ${totals.cycle}` : undefined} />
          </section>

          {totals ? (
            <section className="mt-8 border-b border-slate-200 pb-8">
              <h2 className="text-xl font-black tracking-tight text-slate-900">Campaign Funding</h2>

              <div className="mt-5 border-l-4 border-l-emerald-500 bg-slate-50/50 py-4 pl-5 pr-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm font-semibold text-slate-700">Campaign Spending</div>
                  <div className="text-sm font-semibold text-slate-500">{spentPercentage}% used</div>
                </div>
                <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={classNames(
                      "h-full rounded-full",
                      spentPercentage > 80 ? "bg-red-500" : spentPercentage > 50 ? "bg-amber-500" : "bg-emerald-700"
                    )}
                    style={{ width: `${spentPercentage}%` }}
                  />
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  {money(totals.disbursements)} spent of {money(totals.receipts)} raised
                </div>
              </div>
            </section>
          ) : null}

          {majorContributors.length > 0 ? (
            <section className="mt-8 border-b border-slate-200 pb-8">
              <h2 className="text-xl font-black tracking-tight text-slate-900">Major Individual Donors</h2>
              <p className="mt-1 text-sm text-slate-600">Contributions of $50,000 or more.</p>
              <div className="mt-5 space-y-0">
                {majorContributors.map((contributor, index) => (
                  <div key={`${contributor.contributor_name || "contributor"}-${index}`} className="flex items-center justify-between gap-4 border-b border-slate-100 py-4 last:border-b-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700">
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">
                          {contributor.contributor_name || "Unknown"}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 whitespace-nowrap text-sm font-bold text-emerald-700">
                      {money(contributor.total_contribution)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <div className="mt-8 grid gap-6 xl:grid-cols-2">
            <PACSection title="Supporting This Candidate" subtitle="Super PACs spending money to help this candidate win." items={proPACs} total={totalProSpending} tone="pro" />
            <PACSection title="Opposing This Candidate" subtitle="Super PACs spending money against this candidate." items={antiPACs} total={totalAntiSpending} tone="anti" />
          </div>

          {totalProSpending > 0 || totalAntiSpending > 0 ? (
            <section className="mt-8 border-b border-slate-200 pb-8">
              <h2 className="text-xl font-black tracking-tight text-slate-900">Outside Spending Summary</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="border-l-4 border-l-emerald-500 bg-emerald-50/60 py-4 pl-5">
                  <div className="text-sm font-semibold text-slate-600">For</div>
                  <div className="mt-2 text-2xl font-black text-emerald-700">{compactMoney(totalProSpending)}</div>
                </div>
                <div className="border-l-4 border-l-red-500 bg-red-50/60 py-4 pl-5">
                  <div className="text-sm font-semibold text-slate-600">Against</div>
                  <div className="mt-2 text-2xl font-black text-red-600">{compactMoney(totalAntiSpending)}</div>
                </div>
              </div>
            </section>
          ) : null}

          <section className="mt-8 border-b border-slate-200 pb-8">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-black tracking-tight text-slate-900">Committees</h2>
              <p className="text-sm text-slate-600">
                Authorized and principal committees linked to this candidate (cycle {totals?.cycle || FEC_DEFAULT_CYCLE}).
              </p>
            </div>
            {committees.length > 0 ? (
              <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {committees.map((committee) => (
                  <CommitteeCard key={committee.id || committee.name} committee={committee} />
                ))}
              </div>
            ) : (
              <div className="mt-5 py-4 text-sm text-slate-600">No committees found for this cycle.</div>
            )}
          </section>

          <section className="mt-8 pb-4">
            <h2 className="text-xl font-black tracking-tight text-slate-900">About This Data</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              All figures on this page are for the <strong>{FEC_DEFAULT_CYCLE} election cycle</strong>. Data from the Federal
              Election Commission (FEC) includes campaign fundraising, spending, committees, contributors, and
              outside spending. Super PACs can raise unlimited funds but cannot coordinate with candidates.
            </p>
            <div className="mt-6 flex justify-end">
              <a
                href={fecCandidateUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                View on FEC
                <FaExternalLinkAlt className="h-3 w-3" aria-hidden="true" />
              </a>
            </div>
          </section>
        </div>
      </section>

      <NoCivixAccountModal
        open={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        displayName={candidate?.name}
      />

      <Dialog open={websiteUnavailableOpen} onClose={() => setWebsiteUnavailableOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700, color: "#0f172a" }}>Website Unavailable</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#334155" }}>
            This campaign website appears to be down or no longer available. Would you like to try opening it anyway?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setWebsiteUnavailableOpen(false)}
            sx={{
              textTransform: "none",
              color: "#0f172a",
              borderColor: "#0f172a",
              fontWeight: 600,
              "&:hover": { borderColor: "#0f172a", bgcolor: "rgba(15, 23, 42, 0.04)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingWebsiteUrl) openCampaignSite(pendingWebsiteUrl);
              setWebsiteUnavailableOpen(false);
            }}
            sx={{ textTransform: "none", background: theme.gradient }}
          >
            Open Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
